import { esriStyleToIcon } from '../../modules/EsriStyleToIcon.js';

/**
 * @preserve Copyright (c) 2012, Jason Sanford
 * Google Vector Layers is a library for showing geometry objects
 * from multiple geoweb services with the Google Maps API
 */

/*global gvector */

export let gvector = {};

/**
 * @preserve Using portions of Leaflet code (https://github.com/CloudMade/Leaflet)
 */

/*
 * gvector.Util is a namespace for various utility functions.
 */

gvector.Util = {
    extend: function (/*Object*/ dest) /*-> Object*/ {	// merge src properties into dest
        var sources = Array.prototype.slice.call(arguments, 1);
        for (var j = 0, len = sources.length, src; j < len; j++) {
            src = sources[j] || {};
            for (var i in src) {
                if (src.hasOwnProperty(i)) {
                    dest[i] = src[i];
                }
            }
        }
        return dest;
    },

    setOptions: function (obj, options) {
        obj.options = gvector.Util.extend({}, obj.options, options);
    }

};

/*
 * Class powers the OOP facilities of the library.
 */

/**
 * @preserve Using portions of Leaflet code (https://github.com/CloudMade/Leaflet)
 */

gvector.Class = function () { };

gvector.Class.extend = function (/*Object*/ props) /*-> Class*/ {

    // extended class with the new prototype
    var NewClass = function () {
        if (this.initialize) {
            this.initialize.apply(this, arguments);
        }
    };

    // instantiate class without calling constructor
    var F = function () { };
    F.prototype = this.prototype;
    var proto = new F();

    proto.constructor = NewClass;
    NewClass.prototype = proto;

    // add superclass access
    NewClass.superclass = this.prototype;

    // add class name
    //proto.className = props;

    //inherit parent's statics
    for (var i in this) {
        if (this.hasOwnProperty(i) && i != 'prototype' && i != 'superclass') {
            NewClass[i] = this[i];
        }
    }

    // mix static properties into the class
    if (props.statics) {
        gvector.Util.extend(NewClass, props.statics);
        delete props.statics;
    }

    // mix includes into the prototype
    if (props.includes) {
        gvector.Util.extend.apply(null, [proto].concat(props.includes));
        delete props.includes;
    }

    // merge options
    if (props.options && proto.options) {
        props.options = gvector.Util.extend({}, proto.options, props.options);
    }

    // mix given properties into the prototype
    gvector.Util.extend(proto, props);

    // Commented out this line to avoid the issue of using arguments.callee. Based on this commit change:
    // https://github.com/Leaflet/Leaflet/commit/047402367589c8575b2e39001ded9d437522ffeb#diff-8e4854ab0e220475dc22a076300f7f19f24b4480d852e02177dfd05826e4be5d
	// allow inheriting further
	// NewClass.extend = arguments.callee;

    // method for adding properties to prototype
    NewClass.include = function (props) {
        gvector.Util.extend(this.prototype, props);
    };

    return NewClass;
};

/*
 * gvector.Layer is a base class for rendering vector layers on a Google Maps API map. It's inherited by AGS, A2E, CartoDB, GeoIQ, etc.
 */

gvector.Layer = gvector.Class.extend({

    //
    // Default options for all layers
    //
    options: {
        fields: "",
        scaleRange: null,
        map: null,
        uniqueField: null,
        visibleAtScale: true,
        dynamic: false,
        autoUpdate: false,
        autoUpdateInterval: null,
        infoWindowTemplate: null,
        singleInfoWindow: false,
        symbology: null,
        showAll: false,
        // Added by Katapult
        _loadingCallback: null,
    },

    initialize: function (options) {
        gvector.Util.setOptions(this, options);
    },

    //
    // Show this layer on the map provided
    //
    setMap: function (map) {
        if (map && this.options.map) {
            return;
        }
        this.options.map = map;
        if (map && this.options.scaleRange && this.options.scaleRange instanceof Array && this.options.scaleRange.length === 2) {
            var z = this.options.map.getZoom();
            var sr = this.options.scaleRange;
            this.options.visibleAtScale = (z >= sr[0] && z <= sr[1]);
        }
        this[map ? "_show" : "_hide"]();
    },

    //
    // Get the map (if any) that the layer has been added to
    //
    getMap: function () {
        return this.options.map;
    },

    setOptions: function (o) {
        // TODO - Merge new options (o) with current options (this.options)
    },

    setClickable: function(clickable) {
        this.options.clickable = clickable;
        let geometries = this.getGeometries();
        for (let geometry of geometries) {
            geometry.setOptions({ clickable });
        }
    },

    // Returns a flat list of the geometries
    getGeometries: function() {
        // Create a flat list of geometries
        let geometries = [];
        for (let i = 0; i < this._vectors.length; i++) {
            if (this._vectors[i].vector) {
                geometries.push(this._vectors[i].vector);
            }
            if (this._vectors[i].vectors && this._vectors[i].vectors.length) {
                for (let i2 = 0; i2 < this._vectors[i].vectors.length; i2++) {
                    geometries.push(this._vectors[i].vectors[i2]);
                }
            }
        }
        return geometries;
    },

    getPropertiesForGeometry: function(geometry) {
        for (let i = 0; i < this._vectors.length; i++) {
            if (this._vectors[i].vector == geometry) {
                return this._vectors[i].properties;
            }
            if (this._vectors[i].vectors && this._vectors[i].vectors.length) {
                for (let i2 = 0; i2 < this._vectors[i].vectors.length; i2++) {
                    if (this._vectors[i].vectors[i2] == geometry) {
                        return this._vectors[i].properties;
                    }
                }
            }
        }
    },

    _show: function () {
        this._addIdleListener();
        if (this.options.scaleRange && this.options.scaleRange instanceof Array && this.options.scaleRange.length === 2) {
            this._addZoomChangeListener();
        }
        if (this.options.visibleAtScale) {
            if (this.options.autoUpdate && this.options.autoUpdateInterval) {
                var me = this;
                this._autoUpdateInterval = setInterval(function () {
                    me._getFeatures();
                }, this.options.autoUpdateInterval);
            }
            google.maps.event.trigger(this.options.map, "zoom_changed");
            google.maps.event.trigger(this.options.map, "idle");
        }
    },

    _hide: function () {
        if (this._idleListener) {
            google.maps.event.removeListener(this._idleListener);
        }
        if (this._zoomChangeListener) {
            google.maps.event.removeListener(this._zoomChangeListener);
        }
        if (this._autoUpdateInterval) {
            clearInterval(this._autoUpdateInterval);
        }
        this._clearFeatures();
        this._lastQueriedBounds = null;
        if (this._gotAll) {
            this._gotAll = false;
        }
    },

    //
    // Hide the vectors in the layer. This might get called if the layer is still on but out of scaleRange.
    //
    _hideVectors: function () {
        for (var i = 0; i < this._vectors.length; i++) {
            if (this._vectors[i].vector) {
                this._vectors[i].vector.setMap(null);
                if (this._vectors[i].infoWindow) {
                    this._vectors[i].infoWindow.close()
                } else if (this.infoWindow && this.infoWindow.get("associatedFeature") && this.infoWindow.get("associatedFeature") == this._vectors[i]) {
                    this.infoWindow.close();
                    this.infoWindow = null;
                }
            }
            if (this._vectors[i].vectors && this._vectors[i].vectors.length) {
                for (var i2 = 0; i2 < this._vectors[i].vectors.length; i2++) {
                    this._vectors[i].vectors[i2].setMap(null);
                    if (this._vectors[i].vectors[i2].infoWindow) {
                        this._vectors[i].vectors[i2].infoWindow.close();
                    } else if (this.infoWindow && this.infoWindow.get("associatedFeature") && this.infoWindow.get("associatedFeature") == this._vectors[i]) {
                        this.infoWindow.close();
                        this.infoWindow = null;
                    }
                }
            }
        }
    },

    //
    // Show the vectors in the layer. This might get called if the layer is on and came back into scaleRange.
    //
    _showVectors: function () {
        for (var i = 0; i < this._vectors.length; i++) {
            if (this._vectors[i].vector) {
                this._vectors[i].vector.setMap(this.options.map);
            }
            if (this._vectors[i].vectors && this._vectors[i].vectors.length) {
                for (var i2 = 0; i2 < this._vectors[i].vectors.length; i2++) {
                    this._vectors[i].vectors[i2].setMap(this.options.map);
                }
            }
        }
    },

    //
    // Hide the vectors, then empty the vectory holding array
    //
    _clearFeatures: function () {
        // TODO - Check to see if we even need to hide these before we remove them from the DOM
        this._hideVectors();
        this._vectors = [];
    },

    //
    // Add an event hanlder to detect a zoom change on the map
    //
    _addZoomChangeListener: function () {
        //
        // "this" means something different inside "google.maps.event.addListener" so assign it to "me"
        //
        var me = this;

        //
        // Whenever the map's zoom changes, check the layer's visibility (this.options.visibleAtScale)
        //
        this._zoomChangeListener = google.maps.event.addListener(this.options.map, "zoom_changed", function () {
            me._checkLayerVisibility();
        });
    },

    //
    // Add an event hanlder to detect an idle (pan or zoom) on the map
    //
    _addIdleListener: function () {
        //
        // "this" means something different inside "google.maps.event.addListener" so assign it to "me"
        //
        var me = this;

        //
        // Whenever the map idles (pan or zoom) get the features in the current map extent
        //
        this._idleListener = google.maps.event.addListener(this.options.map, "idle", function () {
            if (me.options.visibleAtScale) {
                //
                // Do they use the showAll parameter to load all features once?
                //
                if (me.options.showAll) {
                    //
                    // Have we already loaded these features
                    //
                    if (!me._gotAll) {
                        //
                        // Grab the features and note that we've already loaded them (no need to _getFeatures again
                        //
                        me._getFeatures();
                        me._gotAll = true;
                    }
                } else {
                    me._getFeatures();
                }
            }
        });
    },

    //
    // Get the current map zoom and check to see if the layer should still be visible
    //
    _checkLayerVisibility: function () {
        //
        // Store current visibility so we can see if it changed
        //
        var visibilityBefore = this.options.visibleAtScale;

        //
        // Check current map scale and see if it's in this layer's range
        //
        var z = this.options.map.getZoom();
        var sr = this.options.scaleRange;
        this.options.visibleAtScale = (z >= sr[0] && z <= sr[1]);

        //
        // Check to see if the visibility has changed
        //
        if (visibilityBefore !== this.options.visibleAtScale) {
            //
            // It did, hide or show vectors
            //
            this[this.options.visibleAtScale ? "_showVectors" : "_hideVectors"]();
        }

        //
        // Check to see if we need to set or clear any intervals for auto-updating layers
        //
        if (visibilityBefore && !this.options.visibleAtScale && this._autoUpdateInterval) {
            clearInterval(this._autoUpdateInterval);
        } else if (!visibilityBefore && this.options.autoUpdate && this.options.autoUpdateInterval) {
            var me = this;
            this._autoUpdateInterval = setInterval(function () {
                me._getFeatures();
            }, this.options.autoUpdateInterval);
        }

    },

    //
    // Set the InfoWindow content for the feature
    //
    _setInfoWindowContent: function (feature) {
        //
        // Store previous InfoWindow content so we can check to see if it changed. If it didn't no sense changing the content as this has an ugly flashing effect.
        //
        var previousContent = feature.iwContent;

        //
        // Ok, let's just pick a name for this please.
        //
        var atts;
        if (feature.attributes) {
            atts = feature.attributes; // Esri
        } else if (feature.properties) {
            atts = feature.properties; // GeoJSON
        } else if (feature.data) {
            atts = feature.data; // GIS Cloud
        }

        var iwContent;

        //
        // Check to see if it's a string-based infoWindowTemplate or function
        //
        if (typeof this.options.infoWindowTemplate == "string") {
            //
            // Store the string-based infoWindowTemplate
            //
            iwContent = this.options.infoWindowTemplate;

            //
            // Loop through the properties and replace mustache-wrapped property names with actual values
            //
            for (var prop in atts) {
                var re = new RegExp("{" + prop + "}", "g");
                iwContent = iwContent.replace(re, atts[prop]);
            }
        } else if (typeof this.options.infoWindowTemplate == "function") {
            //
            // It's a function-based infoWindowTempmlate, so just call this function and pass properties
            //
            iwContent = this.options.infoWindowTemplate(atts);
        } else {
            //
            // Ummm, that's all we support. Seeya!
            //
            return;
        }

        //
        // Store the InfoWindow content
        //
        feature.iwContent = iwContent;

        //
        // If the feature's InfoWindow already exists and the previous content is differentt than the current content, set the content
        //
        if (feature.infoWindow) {
            if (feature.iwContent != previousContent) {
                feature.infoWindow.setContent(feature.iwContent);
            }
        } else if (this.infoWindow && this.infoWindow.get("associatedFeature") == feature) {
            if (feature.iwContent != previousContent) {
                this.infoWindow.setContent(feature.iwContent);
            }
        }
    },

    //
    // Show the feature's (or layer's) InfoWindow
    //
    _showInfoWindow: function (feature, evt) {
        //
        // Set the content
        //
        var infoWindowOptions = {
            content: feature.iwContent
        };

        //
        // Create a variable to hold a reference to the object that owns the InfoWindow so we can show it later
        //
        var ownsInfoWindow;

        //
        // If the layer isn't set to show a single InfoWindow
        //
        if (!this.options.singleInfoWindow) {
            //
            // Create an InfoWindow and store it in the feature
            //
            feature.infoWindow = new google.maps.InfoWindow(infoWindowOptions);
            ownsInfoWindow = feature;
        } else {
            if (this.infoWindow) {
                //
                // If the layer already has an InfoWindow created, close and delete it
                //
                this.infoWindow.close();
                this.infoWindow = null;
            }

            //
            // Create a new InfoWindow
            //
            this.infoWindow = new google.maps.InfoWindow(infoWindowOptions);

            //
            // Store the associated feature reference in the InfoWindow so we can close and clear it later
            //
            this.infoWindow.set("associatedFeature", feature);

            ownsInfoWindow = this;
        }

        //
        // InfoWindows on Lines and Polygons are opened slightly different, make note of it
        //
        var isLineOrPolygon = false;
        if (feature.vector) {
            if (feature.vector.getPaths || feature.vector.getPath) {
                isLineOrPolygon = true;
            }
        } else if (feature.vectors && feature.vectors.length) {
            if (feature.vectors[0].getPaths || feature.vectors[0].getPath) {
                isLineOrPolygon = true
            }
        }

        //
        // "this" means something different inside of the setTimeout function so assigned it to "me"
        //
        var me = this;

        //
        // Don't ask about the InfoWindow.open timeout, I'm not sure why it fails if you open it immediately
        //
        setTimeout(function() {
            ownsInfoWindow.infoWindow.setPosition(evt.latLng);
            ownsInfoWindow.infoWindow.open(me.options.map);
        }, 200);
    },

    //
    // Build a string to pass in a URL for a bbox url parameter (&bbox=-81,35,-80,36)
    //
    _buildBoundsString: function (gBounds) {
        var gBoundsParts = gBounds.toUrlValue().split(",");
        return gBoundsParts[1] + "," + gBoundsParts[0] + "," + gBoundsParts[3] + "," + gBoundsParts[2];
    },

    //
    // Get the appropriate Google Maps vector options for this feature
    //
    _getFeatureVectorOptions: function (feature) {
        //
        // Create an empty vectorOptions object to add to, or leave as is if no symbology can be found
        //
        var vectorOptions = {};

        //
        // Esri calls them attributes. GeoJSON calls them properties.
        //
        var atts = feature.attributes || feature.properties

        //
        // Is there a symbology set for this layer?
        //
        if (this.options.symbology) {
            switch (this.options.symbology.type) {
                case "single":
                    //
                    // It's a single symbology for all features so just set the key/value pairs in vectorOptions
                    //
                    for (var key in this.options.symbology.vectorOptions) {
                        vectorOptions[key] = this.options.symbology.vectorOptions[key];
                    }
                    break;
                case "unique":
                    //
                    // It's a unique symbology. Check if the feature's property value matches that in the symbology and style accordingly
                    //
                    var att = this.options.symbology.property;
                    for (var i = 0, len = this.options.symbology.values.length; i < len; i++) {
                        if (atts[att] == this.options.symbology.values[i].value) {
                            for (var key in this.options.symbology.values[i].vectorOptions) {
                                vectorOptions[key] = this.options.symbology.values[i].vectorOptions[key];
                            }
                        }
                    }
                    break;
                case "range":
                    //
                    // It's a range symbology. Check if the feature's property value is in the range set in the symbology and style accordingly
                    //
                    var att = this.options.symbology.property;
                    for (var i = 0, len = this.options.symbology.ranges.length; i < len; i++) {
                        if (atts[att] >= this.options.symbology.ranges[i].range[0] && atts[att] <= this.options.symbology.ranges[i].range[1]) {
                            for (var key in this.options.symbology.ranges[i].vectorOptions) {
                                vectorOptions[key] = this.options.symbology.ranges[i].vectorOptions[key];
                            }
                        }
                    }
                    break;
            }
        }

        // Apply the clickable state to the options
        vectorOptions.clickable = this.options.clickable;

        return vectorOptions;
    },

    //
    // Check to see if any attributes have changed
    //
    _getPropertiesChanged: function (oldAtts, newAtts) {
        var changed = false;
        for (var key in oldAtts) {
            if (oldAtts[key] != newAtts[key]) {
                changed = true;
            }
        }
        return changed;
    },

    //
    // Check to see if a particular property changed
    //
    _getPropertyChanged: function (oldAtts, newAtts, property) {
        return !(oldAtts[property] == newAtts[property]);
    },

    //
    // Check to see if the geometry has changed
    //
    _getGeometryChanged: function (oldGeom, newGeom) {
        //
        // TODO: make this work for points, linestrings and polygons
        //
        var changed = false;
        if (oldGeom.coordinates && oldGeom.coordinates instanceof Array) {
            //
            // It's GeoJSON
            //

            //
            // For now only checking for point changes
            //
            if (!(oldGeom.coordinates[0] == newGeom.coordinates[0] && oldGeom.coordinates[1] == newGeom.coordinates[1])) {
                changed = true;
            }
        } else {
            //
            // It's EsriJSON
            //

            //
            // For now only checking for point changes
            //
            if (!(oldGeom.x == newGeom.x && oldGeom.y == newGeom.y)) {
                changed = true;
            }
        }
        return changed;
    },

    _makeJsonpRequest: function (url) {
        this.options._loadingCallback?.(true, this);
        var head = document.getElementsByTagName("head")[0];
        var script = document.createElement("script");
        script.type = "text/javascript";
        script.src = url;
        head.appendChild(script);
    },

    _processFeatures: function (data) {
        //
        // Sometimes requests take a while to come back and
        // the user might have turned the layer off
        //
        if (!this.options.map) {
            return;
        }
        var bounds = this.options.map.getBounds();

        // Check to see if the _lastQueriedBounds is the same as the new bounds
        // If true, don't bother querying again.
        if (this._lastQueriedBounds && this._lastQueriedBounds.equals(bounds) && !this.options.autoUpdate) {
            return;
        }

        // Store the bounds in the _lastQueriedBounds member so we don't have
        // to query the layer again if someone simply turns a layer on/off
        this._lastQueriedBounds = bounds;

        // GeoIQ layers return JSON string-wrapped, so JSON.parse it
        if (this instanceof gvector.GeoIQ) {
            data = JSON.parse(data);
        }

        // If necessary, convert data to make it look like a GeoJSON FeatureCollection
        // PRWSF returns GeoJSON, but not in a FeatureCollection. Make it one.
        if (this instanceof gvector.PRWSF) {
            data.features = data.rows;
            delete data.rows;
            for (var i = 0, len = data.features.length; i < len; i++) {
                data.features[i].type = "Feature"; // Not really necessary, but let's follow the GeoJSON spec for a Feature
                data.features[i].properties = {};
                for (var prop in data.features[i].row) {
                    if (prop == "geojson") {
                        data.features[i].geometry = data.features[i].row.geojson;
                    } else {
                        data.features[i].properties[prop] = data.features[i].row[prop];
                    }
                }
                delete data.features[i].row;
            }
        }
        // GISCloud returns GeoJSON, but not in a FeatureCollection. Make it one.
        if (this instanceof gvector.GISCloud) {
            data.features = data.data;
            delete data.data;
            for (var i = 0, len = data.features.length; i < len; i++) {
                data.features[i].type = "Feature"; // Not really necessary, but let's follow the GeoJSON spec for a Feature
                data.features[i].properties = data.features[i].data;
                data.features[i].properties.id = data.features[i].__id;
                delete data.features[i].data;
                data.features[i].geometry = data.features[i].__geometry;
                delete data.features[i].__geometry;
            }
        }

        // If "data.features" exists and there's more than one feature in the array
        if (data && data.features && data.features.length) {

            // Loop through the return features
            for (var i = 0; i < data.features.length; i++) {

                // if AGS layer type assigned "attributes" to "properties" to keep everything looking like GeoJSON Features
                if (this instanceof gvector.EsriJSONLayer) {
                    data.features[i].properties = data.features[i].attributes;
                    delete data.features[i].attributes;
                }

                // All objects are assumed to be false until proven true (remember COPS?)
                var onMap = false;

                // If we have a "uniqueField" for this layer
                if (this.options.uniqueField) {

                    // Loop through all of the features currently on the map
                    for (var i2 = 0; i2 < this._vectors.length; i2++) {

                        // Does the "uniqueField" property for this feature match the feature on the map
                        if (data.features[i].properties[this.options.uniqueField] == this._vectors[i2].properties[this.options.uniqueField]) {
                            // The feature is already on the map
                            onMap = true;

                            // We're only concerned about updating layers that are dynamic (options.dynamic = true).
                            if (this.options.dynamic) {

                                // The feature's geometry might have changed, let's check.
                                if (this._getGeometryChanged(this._vectors[i2].geometry, data.features[i].geometry)) {

                                    // Check to see if it's a point feature, these are the only ones we're updating for now
                                    if (!isNaN(data.features[i].geometry.x || data.features[i].geometry.coordinates[0]) && !isNaN(data.features[i].geometry.y || data.features[i].geometry.coordinates[1])) {
                                        this._vectors[i2].geometry = data.features[i].geometry;
                                        this._vectors[i2].vector.setPosition(new google.maps.LatLng(data.features[i].geometry.y || this._vectors[i2].geometry.coordinates[1], data.features[i].geometry.x || this._vectors[i2].geometry.coordinates[0]));
                                    }

                                }

                                var propertiesChanged = this._getPropertiesChanged(this._vectors[i2].properties, data.features[i].properties);

                                if (propertiesChanged) {
                                    var symbologyPropertyChanged = this._getPropertyChanged(this._vectors[i2].properties, data.features[i].properties, this.options.symbology.property);
                                    this._vectors[i2].properties = data.features[i].properties;
                                    if (this.options.infoWindowTemplate) {
                                        this._setInfoWindowContent(this._vectors[i2]);
                                    }
                                    if (this.options.symbology && this.options.symbology.type != "single" && symbologyPropertyChanged) {
                                        if (this._vectors[i2].vector) {
                                            this._vectors[i2].vector.setOptions(this._getFeatureVectorOptions(this._vectors[i2]));
                                        } else if (this._vectors[i2].vectors) {
                                            for (var i3 = 0, len = this._vectors[i2].vectors.length; i3 < len; i3++) {
                                                this._vectors[i2].vectors[i3].setOptions(this._getFeatureVectorOptions(this._vectors[i2]));
                                            }
                                        }
                                    }
                                }

                            }

                        }

                    }

                }

                // If the feature isn't already or the map OR the "uniqueField" attribute doesn't exist
                if (!onMap || !this.options.uniqueField) {

                    if (this instanceof gvector.GeoJSONLayer) {
                        // Convert GeoJSON to Google Maps vector (Point, Polyline, Polygon)
                        var vector_or_vectors = this._geoJsonGeometryToGoogle(data.features[i].geometry, this._getFeatureVectorOptions(data.features[i]));
                        data.features[i][vector_or_vectors instanceof Array ? "vectors" : "vector"] = vector_or_vectors;
                    } else if (this instanceof gvector.EsriJSONLayer) {
                        // Convert Esri JSON to Google Maps vector (Point, Polyline, Polygon)
                        var vector_or_vectors = this._esriJsonGeometryToGoogle(data.features[i].geometry, this._getFeatureVectorOptions(data.features[i]));
                        data.features[i][vector_or_vectors instanceof Array ? "vectors" : "vector"] = vector_or_vectors;
                    }

                    // Show the vector or vectors on the map
                    if (data.features[i].vector) {
                        data.features[i].vector.setMap(this.options.map);
                    } else if (data.features[i].vectors && data.features[i].vectors.length) {
                        for (var i3 = 0; i3 < data.features[i].vectors.length; i3++) {
                            data.features[i].vectors[i3].setMap(this.options.map);
                        }
                    }

                    // Store the vector in an array so we can remove it later
                    this._vectors.push(data.features[i]);

                    if (this.options.infoWindowTemplate) {

                        var me = this;
                        var feature = data.features[i];

                        this._setInfoWindowContent(feature);

                        (function (feature) {
                            if (feature.vector) {
                                google.maps.event.addListener(feature.vector, "click", function (evt) {
                                    me._showInfoWindow(feature, evt);
                                });
                            } else if (feature.vectors) {
                                for (var i3 = 0, len = feature.vectors.length; i3 < len; i3++) {
                                    google.maps.event.addListener(feature.vectors[i3], "click", function (evt) {
                                        me._showInfoWindow(feature, evt);
                                    });
                                }
                            }
                        }(feature));

                    }

                }

            }

        }

        // Notify that we are done loading
        this.options._loadingCallback?.(false, this);
    }
});

//
// Extend Layer to support GeoJSON geometry parsing
//
gvector.GeoJSONLayer = gvector.Layer.extend({
    //
    // Convert GeoJSON to Google Maps API vectors using portions of https://github.com/JasonSanford/GeoJSON-to-Google-Maps
    //
    _geoJsonGeometryToGoogle: function (geometry, opts) {
        //
        // Create a variable for a single vector and for multi part vectors. The Google Maps API has no real support for these so we keep them in an array.
        //
        var vector, vectors;

        switch (geometry.type) {
            case "Point":
                opts.position = new google.maps.LatLng(geometry.coordinates[1], geometry.coordinates[0]);
                vector = new google.maps.Marker(opts);
                break;

            case "MultiPoint":
                vectors = [];
                for (var i = 0, len = geometry.coordinates.length; i < len; i++) {
                    opts.position = new google.maps.LatLng(geometry.coordinates[i][1], geometry.coordinates[i][0]);
                    vectors.push(new google.maps.Marker(opts));
                }
                break;

            case "LineString":
                var path = [];
                for (var i = 0, len = geometry.coordinates.length; i < len; i++) {
                    var ll = new google.maps.LatLng(geometry.coordinates[i][1], geometry.coordinates[i][0]);
                    path.push(ll);
                }
                opts.path = path;
                vector = new google.maps.Polyline(opts);
                break;

            case "MultiLineString":
                vectors = [];
                for (var i = 0, len = geometry.coordinates.length; i < len; i++) {
                    var path = [];
                    for (var i2 = 0, len2 = geometry.coordinates[i].length; i2 < len2; i2++) {
                        var coord = geometry.coordinates[i][i2];
                        var ll = new google.maps.LatLng(coord[1], coord[0]);
                        path.push(ll);
                    }
                    opts.path = path;
                    vectors.push(new google.maps.Polyline(opts));
                }
                break;

            case "Polygon":
                var paths = [];
                for (var i = 0, len = geometry.coordinates.length; i < len; i++) {
                    var path = [];
                    for (var i2 = 0, len2 = geometry.coordinates[i].length; i2 < len2; i2++) {
                        var ll = new google.maps.LatLng(geometry.coordinates[i][i2][1], geometry.coordinates[i][i2][0]);
                        path.push(ll);
                    }
                    paths.push(path);
                }
                opts.paths = paths;
                vector = new google.maps.Polygon(opts);
                break;

            case "MultiPolygon":
                vectors = [];
                for (var i = 0, len = geometry.coordinates.length; i < len; i++) {
                    paths = [];
                    for (var i2 = 0, len2 = geometry.coordinates[i].length; i2 < len2; i2++) {
                        var path = [];
                        for (var i3 = 0, len3 = geometry.coordinates[i][i2].length; i3 < len3; i3++) {
                            path.push(new google.maps.LatLng(geometry.coordinates[i][i2][i3][1], geometry.coordinates[i][i2][i3][0]));
                        }
                        paths.push(path);
                    }
                    opts.paths = paths;
                    vectors.push(new google.maps.Polygon(opts));
                }
                break;

            case "GeometryCollection":
                vectors = [];
                for (var i = 0, len = geometry.geometries.length; i < len; i++) {
                    vectors.push(this._geoJsonGeometryToGoogle(geometry.geometries[i], opts));
                }
                break;
        }
        return vector || vectors;
    }
});

//
// Extend Layer to support EsriJSON geometry parsing
//
gvector.EsriJSONLayer = gvector.Layer.extend({
    //
    // Turn EsriJSON into Google Maps API vectors
    //
    _esriJsonGeometryToGoogle: function (geometry, opts) {
        //
        // Create a variable for a single vector and for multi part vectors. The Google Maps API has no real support for these so we keep them in an array.
        //
        var vector, vectors;

        if (geometry.x && geometry.y) {
            //
            // A Point
            //
            opts.position = new google.maps.LatLng(geometry.y, geometry.x);
            vector = new google.maps.Marker(opts);
        } else if (geometry.points) {
            //
            // A MultiPoint
            //
            vectors = [];
            for (var i = 0, len = geometry.points.length; i < len; i++) {
                opts.position = new google.maps.LatLng(geometry.points[i].y, geometry.points[i].x);
                vectors.push(new google.maps.Marker(opts));
            }
        } else if (geometry.paths) {
            if (geometry.paths.length > 1) {
                //
                // A MultiLineString
                //
                vectors = [];
                for (var i = 0, len = geometry.paths.length; i < len; i++) {
                    var path = [];
                    for (var i2 = 0, len2 = geometry.paths[i].length; i2 < len2; i2++) {
                        path.push(new google.maps.LatLng(geometry.paths[i][i2][1], geometry.paths[i][i2][0]));
                    }
                    opts.path = path
                    vectors.push(new google.maps.Polyline(opts));
                }
            } else {
                //
                // A LineString
                //
                var path = [];
                for (var i = 0, len = geometry.paths[0].length; i < len; i++) {
                    path.push(new google.maps.LatLng(geometry.paths[0][i][1], geometry.paths[0][i][0]));
                }
                opts.path = path;
                vector = new google.maps.Polyline(opts);
            }
        } else if (geometry.rings) {
            if (geometry.rings.length > 1) {
                //
                // A MultiPolygon
                //
                vectors = [];
                for (var i = 0, len = geometry.rings.length; i < len; i++) {
                    var paths = [];
                    var path = [];
                    for (var i2 = 0, len2 = geometry.rings[i].length; i2 < len2; i2++) {
                        path.push(new google.maps.LatLng(geometry.rings[i][i2][1], geometry.rings[i][i2][0]));
                    }
                    paths.push(path);
                    opts.paths = paths;
                    vectors.push(new google.maps.Polygon(opts));
                }
            } else {
                //
                // A Polygon
                //
                var paths = [];
                var path = [];
                for (var i = 0, len = geometry.rings[0].length; i < len; i++) {
                    path.push(new google.maps.LatLng(geometry.rings[0][i][1], geometry.rings[0][i][0]));
                }
                paths.push(path);
                opts.paths = paths;
                vector = new google.maps.Polygon(opts);
            }
        }
        return vector || vectors;
    }
});

gvector.AGS = gvector.EsriJSONLayer.extend({
    initialize: function (options) {

        // Check for required parameters
        for (var i = 0, len = this._requiredParams.length; i < len; i++) {
            if (!options[this._requiredParams[i]]) {
                throw new Error("No \"" + this._requiredParams[i] + "\" parameter found.");
            }
        }

        // _globalPointer is a string that points to a global function variable
        // Features returned from a JSONP request are passed to this function
        this._globalPointer = "AGS_" + Math.floor(Math.random() * 100000);
        window[this._globalPointer] = this;

        // If the url wasn't passed with a trailing /, add it.
        if (options.url.substr(options.url.length - 1, 1) !== "/") {
            options.url += "/";
        }

        this._originalOptions = gvector.Util.extend({}, options);

        if (options.esriOptions) {
            if (typeof options.esriOptions == "object") {
                gvector.Util.extend(options, this._convertEsriOptions(options.esriOptions));
            } else {
                // Send to function that request JSON from server
                // Use a callback to process returned JSON and send back to initialize layer with proper options
                this._getEsriOptions();
                return; // Get out of here until we have proper JSON
            }
        }

        // Extend Layer to create AGS
        gvector.Layer.prototype.initialize.call(this, options);

        if (this.options.where) {
            this.options.where = encodeURIComponent(this.options.where);
        }

        // Create an array to hold the features
        this._vectors = [];

        if (this.options.map) {
            if (this.options.scaleRange && this.options.scaleRange instanceof Array && this.options.scaleRange.length === 2) {
                var z = this.options.map.getZoom();
                var sr = this.options.scaleRange;
                this.options.visibleAtScale = (z >= sr[0] && z <= sr[1]);
            }
            this._show();
        }
    },

    options: {
        where: "1=1",
        url: null,
        useEsriOptions: false
    },

    _requiredParams: ["url"],

    _convertEsriOptions: function (esriOptions) {
        var gvectorOptions = {};

        // Check to see if minScale and maxScale are present, if so conver to Google Vector Layers format
        if (!(esriOptions.minScale == undefined || esriOptions.maxScale == undefined)) {
            var minScale = this._scaleToLevel(esriOptions.minScale);
            var maxScale = this._scaleToLevel(esriOptions.maxScale);
            if (maxScale == 0) {
                maxScale = 20;
            }
            gvectorOptions.scaleRange = [minScale, maxScale];
        }

        // Check to see if drawingInfo and rendere are present, if so convert to Google Vector Layers format
        if (esriOptions.drawingInfo && esriOptions.drawingInfo.renderer) {
            gvectorOptions.symbology = this._renderOptionsToSymbology(esriOptions.drawingInfo.renderer);
        }

        // TODO: options.infoWindowTemplate

        return gvectorOptions;
    },

    _getEsriOptions: function () {
        this._makeJsonpRequest(this._originalOptions.url + "?f=json&callback=" + this._globalPointer + "._processEsriOptions");
    },

    _processEsriOptions: function (data) {
        var options = this._originalOptions;
        options.esriOptions = data;
        this.initialize(options);
    },

    _scaleToLevel: function (scale) {
        var agsScales = [591657527.591555, 295828763.795777, 147914381.897889, 73957190.948944, 36978595.474472, 18489297.737236, 9244648.868618, 4622324.434309, 2311162.217155, 1155581.108577, 577790.554289, 288895.277144, 144447.638572, 72223.819286, 36111.909643, 18055.954822, 9027.977411, 4513.988705, 2256.994353, 1128.497176, 564.248588, 282.124294];
        if (scale == 0) {
            return 0;
        }
        var level = 0;
        for (var i = 0; i < agsScales.length - 1; i++) {
            var currentScale = agsScales[i];
            var nextScale = agsScales[i + 1];
            if ((scale <= currentScale) && (scale > nextScale)) {
                level = i;
                break;
            }
        }
        return level;
    },

    _renderOptionsToSymbology: function (renderOptions) {
        var symbology = {};
        switch (renderOptions.type) {
            case "simple":
                symbology.type = "single";
                symbology.vectorOptions = this._parseSymbology(renderOptions.symbol);
                break;

            case "uniqueValue":
                symbology.type = "unique";
                symbology.property = renderOptions.field1; //only support single field uniqueValues rends, rarely see multis anyway
                var values = [];
                for (var i = 0; i < renderOptions.uniqueValueInfos.length; i++) {
                    var uvi = renderOptions.uniqueValueInfos[i];
                    var value = {};
                    value.value = uvi.value;
                    value.vectorOptions = this._parseSymbology(uvi.symbol);
                    value.label = uvi.label; //not in gvector spec yet but useful
                    values.push(value);
                }
                symbology.values = values;
                break;

            case "classBreaks":
                symbology.type = "range";
                symbology.property = rend.field;
                var ranges = [];
                var cbrk = renderOptions.minValue;
                for (var i = 0; i < renderOptions.classBreakInfos.length; i++) {
                    var cbi = renderOptions.classBreakInfos[i];
                    var brk = {};
                    brk.range = [cbrk, cbi.classMaxValue];
                    cbrk = cbi.classMaxValue;  //advance
                    brk.vectorOptions = this._parseSymbology(cbi.symbol);
                    brk.label = cbi.label; //not in gvector spec yet but useful
                    ranges.push(brk);
                }
                symbology.ranges = ranges;
                break;
        }
        return symbology;
    },

    _parseSymbology: function (symbol) {
        var vectorOptions = {};
        switch (symbol.type) {
            case "esriSMS":
                vectorOptions.icon = esriStyleToIcon(symbol.style, {
                    fillColor: this._parseColor(symbol.color),
                    fillOpacity: this._parseAlpha(symbol.color[3]),
                    strokeWidth: symbol.outline.width,
                    strokeColor: this._parseColor(symbol.outline.color),
                    strokeOpacity: this._parseAlpha(symbol.outline.color[3])
                });
                vectorOptions.optimized = true;
                vectorOptions.draggable = false;
                break;
            case "esriPMS":
                //TODO marker symbologys have an url prop as well but requires extra hops to server for all icons
                var url = "data:" + symbol.contentType + ";base64," + symbol.imageData;
                vectorOptions.icon = new google.maps.MarkerImage(url,
                    null /* size */,
                    null /* origin */,
                    new google.maps.Point(symbol.width / 2, symbol.height / 2) /* anchor */);
                break;

            case "esriSLS":
                //we can only do solid lines in GM (true in latest build?)
                vectorOptions.strokeWeight = symbol.width;
                vectorOptions.strokeColor = this._parseColor(symbol.color);
                vectorOptions.strokeOpacity = this._parseAlpha(symbol.color[3]);
                break;

            case "esriSFS":
                //solid or hollow only
                if (symbol.outline) {
                    vectorOptions.strokeWeight = symbol.outline.width;
                    vectorOptions.strokeColor = this._parseColor(symbol.outline.color);
                    vectorOptions.strokeOpacity = this._parseAlpha(symbol.outline.color[3]);
                } else {
                    vectorOptions.strokeWeight = 0;
                    vectorOptions.strokeColor = "#000000";
                    vectorOptions.strokeOpacity = 0.0;
                }
                if (symbol.style != "esriSFSNull") {
                    vectorOptions.fillColor = this._parseColor(symbol.color);
                    vectorOptions.fillOpacity = this._parseAlpha(symbol.color[3]);
                } else {
                    vectorOptions.fillColor = "#000000";
                    vectorOptions.fillOpacity = 0.0;
                }
                break;
        }
        return vectorOptions;
    },

    _parseColor: function (color) {
        const red = this._normalize(color[0]);
        const green = this._normalize(color[1]);
        const blue = this._normalize(color[2]);
        return '#' + this._pad(red.toString(16)) + this._pad(green.toString(16)) + this._pad(blue.toString(16));
    },

    _normalize: function (color) {
        return (color < 1.0 && color > 0.0) ? Math.floor(color * 255) : color;
    },

    _pad: function (s) {
        return s.length > 1 ? s.toUpperCase() : "0" + s.toUpperCase();
    },

    _parseAlpha: function (a) {
        // 0-255 -> 0-1.0
        return (a / 255);
    },

    _getFeatures: function () {
        // If we don't have a uniqueField value it's hard to tell if new features are duplicates so clear them all
        if (!this.options.uniqueField) {
            this._clearFeatures();
        }

        // Build URL
        var url = this.options.url + "query" + // Query this layer
            "?returnGeometry=true" + // Of course we want geometry
            "&outSR=4326" + // receive geometry in WGS 84 Lat/Lng.
            "&f=json" + // Wish it were GeoJSON, but we'll take it
            "&outFields=" + this.options.fields + // Please return the following fields
            "&where=" + this.options.where + // By default return all feature (1=1) but could pass SQL statement (value<90)
            "&callback=" + this._globalPointer + "._processFeatures"; // Need this for JSONP
        if (!this.options.showAll) {
            url += "&inSR=4326" + // request geometry in WGS 84 Lat/Lng.
                "&spatialRel=esriSpatialRelIntersects" + // Find stuff that intersects this envelope
                "&geometryType=esriGeometryEnvelope" + // Our "geometry" url param will be an envelope
                "&geometry=" + this._buildBoundsString(this.options.map.getBounds()); // Build envelope geometry
        }

        // JSONP request
        this._makeJsonpRequest(url);
    }
});

gvector.A2E = gvector.AGS.extend({
    initialize: function (options) {

        // Check for required parameters
        for (var i = 0, len = this._requiredParams.length; i < len; i++) {
            if (!options[this._requiredParams[i]]) {
                throw new Error("No \"" + this._requiredParams[i] + "\" parameter found.");
            }
        }

        // _globalPointer is a string that points to a global function variable
        // Features returned from a JSONP request are passed to this function
        this._globalPointer = "A2E_" + Math.floor(Math.random() * 100000);
        window[this._globalPointer] = this;

        // If the url wasn't passed with a trailing /, add it.
        if (options.url.substr(options.url.length - 1, 1) !== "/") {
            options.url += "/";
        }

        this._originalOptions = gvector.Util.extend({}, options);

        if (options.esriOptions) {
            if (typeof options.esriOptions == "object") {
                gvector.Util.extend(options, this._convertEsriOptions(options.esriOptions));
            } else {
                // Send to function that request JSON from server
                // Use a callback to process returned JSON and send back to initialize layer with proper options
                this._getEsriOptions();
                return; // Get out of here until we have proper JSON
            }
        }

        // Extend Layer to create A2E
        gvector.Layer.prototype.initialize.call(this, options);

        if (this.options.where) {
            this.options.where = encodeURIComponent(this.options.where);
        }

        // Create an array to hold the features
        this._vectors = [];

        if (this.options.map) {
            if (this.options.scaleRange && this.options.scaleRange instanceof Array && this.options.scaleRange.length === 2) {
                var z = this.options.map.getZoom();
                var sr = this.options.scaleRange;
                this.options.visibleAtScale = (z >= sr[0] && z <= sr[1]);
            }
            this._show();
        }

        //
        // Check for an autoUpdate option and pubnub info to pull live updates
        //
        if (this.options.autoUpdate && this.options.esriOptions.editFeedInfo) {
            //Load the pubnub JavaScript API
            this._makeJsonpRequest("http://cdn.pubnub.com/pubnub-3.1.min.js");

            // Keep checking to see when the library successfully loaded
            var me = this;
            this._pubNubScriptLoaderInterval = setInterval(function () {
                if (window.PUBNUB) {
                    me._pubNubScriptLoaded();
                }
            }, 200);
        }
    },

    _pubNubScriptLoaded: function () {
        // Stop checking for the pubnub script
        clearInterval(this._pubNubScriptLoaderInterval);

        // Initialize pubnub script
        this.pubNub = PUBNUB.init({
            subscribe_key: this.options.esriOptions.editFeedInfo.pubnubSubscribeKey,
            ssl: false,
            origin: "pubsub.pubnub.com"
        });

        var me = this;

        // Subscribe to changes
        this.pubNub.subscribe({
            channel: this.options.esriOptions.editFeedInfo.pubnubChannel,
            callback: function (message) {
                me._getFeatures();
            },
            error: function () {

            }
        });
    }

});

gvector.GeoIQ = gvector.GeoJSONLayer.extend({
    initialize: function (options) {

        // Check for required parameters
        for (var i = 0, len = this._requiredParams.length; i < len; i++) {
            if (!options[this._requiredParams[i]]) {
                throw new Error("No \"" + this._requiredParams[i] + "\" parameter found.");
            }
        }

        // Extend Layer to create GeoIQ
        gvector.Layer.prototype.initialize.call(this, options);

        // _globalPointer is a string that points to a global function variable
        // Features returned from a JSONP request are passed to this function
        this._globalPointer = "GeoIQ_" + Math.floor(Math.random() * 100000);
        window[this._globalPointer] = this;

        // Create an array to hold the features
        this._vectors = [];


        if (this.options.map) {
            if (this.options.scaleRange && this.options.scaleRange instanceof Array && this.options.scaleRange.length === 2) {
                var z = this.options.map.getZoom();
                var sr = this.options.scaleRange;
                this.options.visibleAtScale = (z >= sr[0] && z <= sr[1]);
            }
            this._show();
        }
    },

    options: {
        dataset: null
    },

    _requiredParams: ["dataset"],

    _getFeatures: function () {
        // If we don't have a uniqueField value it's hard to tell if new features are duplicates so clear them all
        if (!this.options.uniqueField) {
            this._clearFeatures();
        }

        // Build URL
        var url = "http://geocommons.com/datasets/" + this.options.dataset + // Geocommons dataset ID
            "/features.json?" + // JSON please
            "geojson=1" + // Return GeoJSON formatted data
            "&callback=" + this._globalPointer + "._processFeatures" + // Need this for JSONP
            "&limit=999"; // Don't limit our results
        if (!this.options.showAll) {
            url += "&bbox=" + this._buildBoundsString(this.options.map.getBounds()) + // Build bbox geometry
                "&intersect=full"; // Return features that intersect this bbox, not just fully contained
        }

        // JSONP request
        this._makeJsonpRequest(url);

    }
});

gvector.CartoDB = gvector.GeoJSONLayer.extend({
    initialize: function (options) {

        // Check for required parameters
        for (var i = 0, len = this._requiredParams.length; i < len; i++) {
            if (!options[this._requiredParams[i]]) {
                throw new Error("No \"" + this._requiredParams[i] + "\" parameter found.");
            }
        }

        // Extend Layer to create CartoDB
        gvector.Layer.prototype.initialize.call(this, options);

        // _globalPointer is a string that points to a global function variable
        // Features returned from a JSONP request are passed to this function
        this._globalPointer = "CartoDB_" + Math.floor(Math.random() * 100000);
        window[this._globalPointer] = this;

        // Create an array to hold the features
        this._vectors = [];


        if (this.options.map) {
            if (this.options.scaleRange && this.options.scaleRange instanceof Array && this.options.scaleRange.length === 2) {
                var z = this.options.map.getZoom();
                var sr = this.options.scaleRange;
                this.options.visibleAtScale = (z >= sr[0] && z <= sr[1]);
            }
            this._show();
        }
    },

    options: {
        version: 1,
        user: null,
        table: null,
        fields: "*",
        where: null,
        limit: null,
        uniqueField: "cartodb_id"
    },

    _requiredParams: ["user", "table"],

    _getFeatures: function () {
        // Build Query
        var where = this.options.where || "";
        if (!this.options.showAll) {
            var bounds = this.options.map.getBounds();
            var sw = bounds.getSouthWest();
            var ne = bounds.getNorthEast();
            var tableCount = this.options.table.split(",").length;
            for (var i = 0; i < tableCount; i++) {
                where += (where.length ? " AND " : "") + (tableCount > 1 ? this.options.table.split(",")[i].split(".")[0] + ".the_geom" : "the_geom") + " && st_setsrid(st_makebox2d(st_point(" + sw.lng() + "," + sw.lat() + "),st_point(" + ne.lng() + "," + ne.lat() + ")),4326)";
            }
        }
        if (this.options.limit) {
            where += (where.length ? " " : "") + "limit " + this.options.limit;
        }
        where = (where.length ? " " + where : "");
        var query = "SELECT " + this.options.fields + " FROM " + this.options.table + (where.length ? " WHERE " + where : "");

        // Build URL
        var url = "http://" + this.options.user + ".cartodb.com/api/v" + this.options.version + "/sql" + // The API entry point
            "?q=" + encodeURIComponent(query) + // The SQL statement
            "&format=geojson" + // GeoJSON please
            "&callback=" + this._globalPointer + "._processFeatures"; // Need this for JSONP

        // JSONP request
        this._makeJsonpRequest(url);
    }

});

gvector.PRWSF = gvector.GeoJSONLayer.extend({
    initialize: function (options) {

        // Check for required parameters
        for (var i = 0, len = this._requiredParams.length; i < len; i++) {
            if (!options[this._requiredParams[i]]) {
                throw new Error("No \"" + this._requiredParams[i] + "\" parameter found.");
            }
        }

        // If the url wasn't passed with a trailing /, add it.
        if (options.url.substr(options.url.length - 1, 1) !== "/") {
            options.url += "/";
        }

        // Extend Layer to create PRWSF
        gvector.Layer.prototype.initialize.call(this, options);

        // _globalPointer is a string that points to a global function variable
        // Features returned from a JSONP request are passed to this function
        this._globalPointer = "PRWSF_" + Math.floor(Math.random() * 100000);
        window[this._globalPointer] = this;

        // Create an array to hold the features
        this._vectors = [];


        if (this.options.map) {
            if (this.options.scaleRange && this.options.scaleRange instanceof Array && this.options.scaleRange.length === 2) {
                var z = this.options.map.getZoom();
                var sr = this.options.scaleRange;
                this.options.visibleAtScale = (z >= sr[0] && z <= sr[1]);
            }
            this._show();
        }
    },

    options: {
        geotable: null,
        srid: null,
        geomFieldName: "the_geom",
        fields: "",
        where: null,
        limit: null,
        uniqueField: null
    },

    _requiredParams: ["url", "geotable"],

    _getFeatures: function () {

        // Build Query
        var where = this.options.where || "";
        if (!this.options.showAll) {
            var bounds = this.options.map.getBounds();
            var sw = bounds.getSouthWest();
            var ne = bounds.getNorthEast();
            where += where.length ? " AND " : "";
            if (this.options.srid) {
                where += this.options.geomFieldName + " && transform(st_setsrid(st_makebox2d(st_point(" + sw.lng() + "," + sw.lat() + "),st_point(" + ne.lng() + "," + ne.lat() + ")),4326)," + this.options.srid + ")";
            } else {
                where += "transform(" + this.options.geomFieldName + ",4326) && st_setsrid(st_makebox2d(st_point(" + sw.lng() + "," + sw.lat() + "),st_point(" + ne.lng() + "," + ne.lat() + ")),4326)";
            }
        }

        // Limit returned features
        if (this.options.limit) {
            where += (where.length ? " " : "") + "limit " + this.options.limit;
        }

        // Build fields
        var fields = (this.options.fields.length ? this.options.fields + "," : "") + "st_asgeojson(transform(" + this.options.geomFieldName + ",4326)) as geojson";

        // Build URL
        var url = this.options.url + "v1/ws_geo_attributequery.php" + // The attribute query service
            "?parameters=" + encodeURIComponent(where) + // The SQL where statement
            "&geotable=" + this.options.geotable + // The table name
            "&fields=" + encodeURIComponent(fields) + //
            "&format=json" + // JSON please
            "&callback=" + this._globalPointer + "._processFeatures"; // Need this for JSONP

        // JSONP request
        this._makeJsonpRequest(url);
    }
});

gvector.GISCloud = gvector.GeoJSONLayer.extend({
    initialize: function (options) {

        // Check for required parameters
        for (var i = 0, len = this._requiredParams.length; i < len; i++) {
            if (!options[this._requiredParams[i]]) {
                throw new Error("No \"" + this._requiredParams[i] + "\" parameter found.");
            }
        }

        // Extend Layer to create GISCloud
        gvector.Layer.prototype.initialize.call(this, options);

        // _globalPointer is a string that points to a global function variable
        // Features returned from a JSONP request are passed to this function
        this._globalPointer = "GISCloud_" + Math.floor(Math.random() * 100000);
        window[this._globalPointer] = this;

        // Create an array to hold the features
        this._vectors = [];


        if (this.options.map) {
            if (this.options.scaleRange && this.options.scaleRange instanceof Array && this.options.scaleRange.length === 2) {
                var z = this.options.map.getZoom();
                var sr = this.options.scaleRange;
                this.options.visibleAtScale = (z >= sr[0] && z <= sr[1]);
            }
            this._show();
        }
    },

    options: {
        mapID: null,
        layerID: null,
        uniqueField: "id"
    },

    _requiredParams: ["mapID", "layerID"],

    _getFeatures: function () {

        // Build URL
        var url = "http://api.giscloud.com/1/maps/" + this.options.mapID + // GISCloud Map ID
            "/layers/" + this.options.layerID +
            "/features.json?" + // JSON please
            "geometry=geojson" + // Return GeoJSON formatted data
            "&epsg=4326" + // Using Lat Lng for bounding box units
            "&callback=" + this._globalPointer + "._processFeatures"; // Need this for JSONP
        if (!this.options.showAll) {
            url += "&bounds=" + this._buildBoundsString(this.options.map.getBounds()); // Build bbox geometry
        }
        if (this.options.where) {
            url += "&where=" + encodeURIComponent(this.options.where);
        }

        // JSONP request
        this._makeJsonpRequest(url);

    }
});